// src/modules/socialhive/mobile/MobileEventsHome.jsx
import React, { useCallback, useContext, useEffect, useState } from 'react';
import './MobileEventsHome.css';
import { GeneralContext } from '../../GeneralProvider';
import Services from '../../services/Services';
import { useLanguage } from '../../LanguageProvider';
import { useNavigate } from 'react-router-dom'; // <<< EK

// ---- helpers ----
function uniqByEventID(list) {
    if (!Array.isArray(list)) return [];
    const map = new Map();

    for (const item of list) {
        const ev = item?.event || item;
        const key = ev?.eventID || ev?.id;

        if (!key) {
            const randKey = Symbol('no-key');
            map.set(randKey, item);
            continue;
        }

        if (!map.has(key)) {
            map.set(key, item);
        }
    }

    return Array.from(map.values());
}

function safeParse(v) {
    if (!v) return null;
    if (typeof v === 'number' || /^\d+$/.test(String(v))) {
        const num = Number(v);
        const ms = String(num).length === 10 ? num * 1000 : num;
        const d = new Date(ms);
        return isNaN(d.getTime()) ? null : d;
    }
    if (typeof v === 'string' && v.includes(' ') && !v.includes('T')) {
        const d = new Date(v.replace(' ', 'T'));
        return isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
}

function safeParseDate(input, fallbackTime) {
    if (!input && fallbackTime) input = fallbackTime;
    if (!input) return null;

    if (typeof input === 'number' || /^\d+$/.test(String(input))) {
        const num = Number(input);
        const ms = String(num).length === 10 ? num * 1000 : num;
        const d = new Date(ms);
        return isNaN(d.getTime()) ? null : d;
    }

    if (typeof input === 'string' && input.includes(' ') && !input.includes('T')) {
        const d = new Date(input.replace(' ', 'T'));
        if (!isNaN(d.getTime())) return d;
    }

    const d = new Date(input);
    if (!isNaN(d.getTime())) return d;

    if (typeof input === 'object' && input?.date && input?.time) {
        const d2 = new Date(`${input.date}T${input.time}`);
        return isNaN(d2.getTime()) ? null : d2;
    }
    return null;
}

function pickStartDate(ev) {
    return (
        ev?.eventStartDateTime ||
        ev?.startDateTime ||
        ev?.startDate ||
        ev?.start ||
        ev?.date ||
        ev?.startsAt ||
        (ev?.startDate && ev?.startTime && `${ev.startDate}T${ev.startTime}`) ||
        null
    );
}

const sortByStart = (a, b) => {
    const ta = safeParseDate(pickStartDate(a))?.getTime() ?? 0;
    const tb = safeParseDate(pickStartDate(b))?.getTime() ?? 0;
    return ta - tb;
};

function formatJoinedDate(isoLike, getTranslation) {
    const d = safeParse(isoLike);
    if (!d) return '-';

    const weekdayKey = d
        .toLocaleDateString('en-US', { weekday: 'long' })
        .toLowerCase();
    const monthKey = d
        .toLocaleDateString('en-US', { month: 'long' })
        .toLowerCase();

    const weekday = getTranslation(weekdayKey) || weekdayKey;
    const month = getTranslation(monthKey) || monthKey;

    const day = String(d.getDate()).padStart(2, '0');
    const hour = String(d.getHours()).padStart(2, '0');
    const minute = String(d.getMinutes()).padStart(2, '0');

    return `${weekday}, ${day} ${month} ${hour}:${minute}`;
}

function getLocationLabel(ev, getTranslation) {
    if (ev?.isOnline === '1' || ev?.isOnline === 1) {
        return getTranslation('online') || 'Online';
    }
    return ev?.locationAddress || ev?.location || '';
}

function getBanner(ev) {
    return ev?.bannerImage || ev?.cover || ev?.image || ev?.thumbnail;
}

export default function MobileEventsHome({ token, setShowHome }) {
    const { getTranslation } = useLanguage();
    const { userId } = useContext(GeneralContext) || {};
    const [joinedEvents, setJoinedEvents] = useState([]);
    const [popularEvents, setPopularEvents] = useState([]);
    const [loadingJoined, setLoadingJoined] = useState(false);
    const [loadingPopular, setLoadingPopular] = useState(false);
    const navigate = useNavigate();
    const openEventDetails = (ev) => {
        const id = ev?.eventID || ev?.id;
        if (!id) return;
        setShowHome(false)
        navigate(`/event/${id}/details`);
    };

    const fetchJoinedEvents = useCallback(() => {
        if (!token || !userId) return;
        setLoadingJoined(true);
        Services.SocialHiveGetUsersEvents(token, userId)
            .then((res) => {
                if (res?.data?.status && Array.isArray(res?.data?.result)) {
                    const raw = res.data.result;
                    const unique = uniqByEventID(raw).sort(sortByStart);
                    setJoinedEvents(unique);
                } else {
                    setJoinedEvents([]);
                }
            })
            .catch(() => {
                setJoinedEvents([]);
            })
            .finally(() => setLoadingJoined(false));
    }, [token, userId]);

    const fetchPopularEvents = useCallback(() => {
        if (!token) return;
        setLoadingPopular(true);
        Services.SocialHiveGetPopularChannelEvents(token)
            .then((res) => {
                const list = Array.isArray(res?.data?.result) ? res.data.result : [];
                const unique = uniqByEventID(list).sort(sortByStart);
                setPopularEvents(unique);
            })
            .catch(() => {
                setPopularEvents([]);
            })
            .finally(() => setLoadingPopular(false));
    }, [token]);

    useEffect(() => {
        fetchJoinedEvents();
        fetchPopularEvents();
    }, [fetchJoinedEvents, fetchPopularEvents]);

    return (
        <div className="mleh-page">
            <main className="mleh-body">
                {/* Katıldığın Etkinlikler - Carousel */}
                <section className="mleh-section">
                    <div className="mleh-section-head">
                        <h2 className="mleh-section-title">
                            {getTranslation('joinedEvents') || 'Katıldığın Etkinlikler'}
                        </h2>
                    </div>

                    {loadingJoined ? (
                        <div className="mleh-loading">
                            {getTranslation('loading') || 'Yükleniyor...'}
                        </div>
                    ) : joinedEvents.length ? (
                        <div className="mleh-carousel">
                            <div className="mleh-carousel-track">
                                {joinedEvents.map((ev) => (
                                    <button
                                        key={ev?.eventID || ev?.id}
                                        type="button"
                                        className="mleh-event-card mleh-event-card--wide"
                                        onClick={() => openEventDetails(ev)}
                                    >
                                        <div className="mleh-event-media">
                                            <img
                                                src={getBanner(ev)}
                                                alt={ev?.eventTitle || ev?.title || 'event'}
                                                onError={(e) => {
                                                    e.currentTarget.src =
                                                        "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='320' height='180'><rect width='100%' height='100%' fill='%23222'/></svg>";
                                                }}
                                            />
                                        </div>
                                        <div className="mleh-event-body">
                                            <div className="mleh-event-title">
                                                {ev?.eventTitle || ev?.title || '-'}
                                            </div>
                                            <div className="mleh-event-meta">
                                                <div className="mleh-event-meta-row">
                                                    <span>
                                                        {formatJoinedDate(pickStartDate(ev), getTranslation)} - {getLocationLabel(ev, getTranslation) || '-'}
                                                    </span>
                                                </div>

                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="mleh-empty">
                            {getTranslation('noJoinedEvents') ||
                                'Katıldığın etkinlik bulunmuyor.'}
                        </div>
                    )}
                </section>

                {/* Popüler Etkinlikler - Liste */}
                <section className="mleh-section">
                    <div className="mleh-section-head">
                        <h2 className="mleh-section-title">
                            {getTranslation('popularEvents') || 'Popüler Etkinlikler'}
                        </h2>
                    </div>

                    {loadingPopular ? (
                        <div className="mleh-loading">
                            {getTranslation('loading') || 'Yükleniyor...'}
                        </div>
                    ) : popularEvents.length ? (
                        <div className="mleh-list">
                            {popularEvents.map((ev) => {
                                const participantCount =
                                    ev?.participantsCount ??
                                    (ev?.userCount ? parseInt(ev.userCount, 10) : null) ??
                                    (ev?.user_count ? parseInt(ev.user_count, 10) : null);

                                const firstTicket = Array.isArray(ev?.tickets)
                                    ? ev.tickets[0]
                                    : null;

                                const isFree =
                                    Number(ev?.isFree) === 1 ||
                                    (firstTicket && Number(firstTicket.price) === 0);

                                const eventIsFree = String(ev?.isFree ?? "0");
                                const hasTickets = String(ev?.hasTickets ?? "0");

                                const isFreeBadge =
                                    eventIsFree === "0" ||
                                    eventIsFree === "1" ||
                                    hasTickets !== "1";

                                const isPaidBadge =
                                    eventIsFree === "2" && hasTickets === "1";

                                const isFreePaidBadge =
                                    eventIsFree === "3" && hasTickets === "1";
                                return (
                                    <button
                                        key={ev?.eventID || ev?.id}
                                        type="button"
                                        className="mleh-event-card mleh-event-card--wide mleh-event-card--list"
                                        onClick={() => openEventDetails(ev)}
                                    >
                                        <div className="mleh-event-media">
                                            <img
                                                src={getBanner(ev)}
                                                alt={ev?.eventTitle || ev?.title || 'event'}
                                                onError={(e) => {
                                                    e.currentTarget.src =
                                                        "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='320' height='180'><rect width='100%' height='100%' fill='%23222'/></svg>";
                                                }}
                                            />
                                        </div>

                                        <div className="mleh-event-body">
                                            <div className="mleh-event-title">
                                                {ev?.eventTitle || ev?.title || '-'}
                                            </div>

                                            <div className="mleh-event-meta">
                                                <div className="mleh-event-meta">
                                                    {/* Tarih + Lokasyon */}
                                                    <div className="mleh-event-meta-row">
                                                        <span>
                                                            {formatJoinedDate(pickStartDate(ev), getTranslation)}
                                                            {" - "}
                                                            {getLocationLabel(ev, getTranslation) || "-"}
                                                        </span>
                                                    </div>

                                                    {/* Katılımcı + Fiyat badge'leri yan yana */}
                                                    <div className="mleh-event-meta-row mleh-badge-row">
                                                        {participantCount != null && participantCount > 0 && (
                                                            <div className="mleh-meta-badge">
                                                                <span>{participantCount}</span>
                                                                <span>{getTranslation("participant") || "Katılımcı"}</span>
                                                            </div>
                                                        )}

                                                        <div
                                                            className={
                                                                "mleh-meta-badge " +
                                                                (isFreeBadge ? "free" : "") +
                                                                ((isPaidBadge || isFreePaidBadge) ? " paid" : "")
                                                            }
                                                        >
                                                            <span>
                                                                {isFreeBadge && (getTranslation("free") || "Ücretsiz")}
                                                                {isPaidBadge && (getTranslation("paid") || "Ücretli")}
                                                                {isFreePaidBadge &&
                                                                    (getTranslation("freePaid") || "Ücretsiz & Ücretli")}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="mleh-empty">
                            {getTranslation('noUpcomingEvents') ||
                                'Gösterilecek popüler etkinlik bulunmuyor.'}
                        </div>
                    )}
                </section>
            </main>
        </div>
    );
}